import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DevicesCard from "@/components/DevicesCard";
import { useAuth } from "@/contexts/AuthContext";
import PhoneOtpDialog from "@/components/PhoneOtpDialog";
import { phoneLoginConfig } from "@/lib/firebasePhone";
import api, { formatApiErrorDetail, setAccessToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AddressForm } from "@/components/AddressPicker";
import { toast } from "sonner";
import { UserCircle2, KeyRound, MapPin, Plus, Edit3, Trash2, Star, Loader2, ShieldCheck, Smartphone } from "lucide-react";

import { logger } from "@/lib/logger";
export default function CustomerProfile({ embedded = false, section = null }) {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(section || (!embedded && params.get("tab")) || "details");

  if (!user) return <div className="max-w-3xl mx-auto px-6 py-10">Loading…</div>;

  return (
    <div className={embedded ? "max-w-4xl" : "max-w-4xl mx-auto px-4 sm:px-6 py-8"}>
      <div className="mb-6">
        {!embedded && <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Your profile</h1>}
        <p className="text-sm text-stone-600 mt-2">Signed in as {user.email || user.phone}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full bg-stone-100 p-1 h-12 max-w-full overflow-x-auto justify-start [scrollbar-width:none]">
          <TabsTrigger value="details" data-testid="profile-tab-details" className="rounded-full px-6">
            <UserCircle2 className="h-4 w-4 mr-1.5" /> Details
          </TabsTrigger>
          <TabsTrigger value="password" data-testid="profile-tab-password" className="rounded-full px-6">
            <KeyRound className="h-4 w-4 mr-1.5" /> Password
          </TabsTrigger>
          <TabsTrigger value="addresses" data-testid="profile-tab-addresses" className="rounded-full px-6">
            <MapPin className="h-4 w-4 mr-1.5" /> Addresses
          </TabsTrigger>
          <TabsTrigger value="devices" data-testid="profile-tab-devices" className="rounded-full px-6">
            <Smartphone className="h-4 w-4 mr-1.5" /> Devices
          </TabsTrigger>
          <TabsTrigger value="privacy" data-testid="profile-tab-privacy" className="rounded-full px-6">
            <ShieldCheck className="h-4 w-4 mr-1.5" /> Privacy
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <ProfileDetailsCard user={user} onSaved={refresh} />
        </TabsContent>
        <TabsContent value="password" className="mt-6">
          {user.has_password === false ? <SetPasswordCard user={user} onSaved={refresh} /> : <ChangePasswordCard />}
        </TabsContent>
        <TabsContent value="addresses" className="mt-6">
          <AddressesCard />
        </TabsContent>
        <TabsContent value="devices" className="mt-6">
          <DevicesCard />
        </TabsContent>
        <TabsContent value="privacy" className="mt-6">
          <PrivacyCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProfileDetailsCard({ user, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
  const [currentPassword, setCurrentPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const emailChanged = form.email.toLowerCase() !== (user.email || "").toLowerCase();
  // Mobile verification needs SMS, which is off while messages go by email.
  const [smsEnabled, setSmsEnabled] = useState(false);
  // Mobile OTP (Firebase): verify/change the number by SMS code instead of typing it.
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpFor, setOtpFor] = useState(null);   // "link" | "reauth"
  useEffect(() => {
    api.get("/config/defaults").then(({ data }) => setSmsEnabled(!!data.sms_enabled)).catch(() => {});
    phoneLoginConfig().then(c => setOtpEnabled(!!c.enabled));
  }, []);
  const phoneChanged = !otpEnabled && form.phone !== (user.phone || "");
  const noPassword = user.has_password === false;
  const nameChanged = form.name !== (user.name || "");
  const sensitiveChange = emailChanged || phoneChanged;
  const anyChange = nameChanged || sensitiveChange;

  const submit = async () => {
    if (!anyChange) return toast.info("Nothing to save");
    if (sensitiveChange && noPassword) { setOtpFor("reauth"); return; }
    if (sensitiveChange && !currentPassword) return toast.error("Enter your current password to change email or phone");
    await save();
  };

  const save = async (reauthIdToken) => {
    setBusy(true);
    try {
      await api.patch("/customer/profile", {
        name: form.name,
        email: form.email,
        phone: otpEnabled ? undefined : form.phone,   // with OTP the number changes only via "Verify"
        current_password: sensitiveChange && !noPassword ? currentPassword : undefined,
        reauth_id_token: reauthIdToken,
      });
      toast.success("Profile updated");
      setCurrentPassword("");
      await onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-2xl border-stone-200 max-w-2xl">
      <div className="space-y-4">
        <div>
          <Label>Full name</Label>
          <Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="h-11 mt-1" />
        </div>
        <div>
          <Label>Email</Label>
          <Input data-testid="profile-email-input" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="h-11 mt-1" />
          {emailChanged ? <div className="text-xs text-marigold mt-1">Changing email will require your current password.</div>
            : <VerifyField channel="email" verified={user.email_verified} onVerified={onSaved} />}
        </div>
        <div>
          <Label>Phone</Label>
          {otpEnabled ? (
            <div className="flex items-center gap-2 mt-1">
              <Input value={user.phone || ""} readOnly placeholder="No mobile number yet" className="h-11 bg-stone-50" data-testid="profile-phone-input" />
              <Button type="button" variant="outline" className="h-11 rounded-full shrink-0" onClick={() => setOtpFor("link")} data-testid="profile-phone-otp">
                {user.phone ? (user.phone_verified ? "Change" : "Verify") : "Add"}
              </Button>
            </div>
          ) : (
          <Input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="h-11 mt-1" placeholder="+91…" />
          )}
          {otpEnabled ? (user.phone_verified && <div className="text-xs text-teal mt-1">Verified — you can sign in with this number and an SMS code.</div>)
            : phoneChanged ? <div className="text-xs text-marigold mt-1">Changing phone will require your current password.</div>
            : user.phone && smsEnabled && <VerifyField channel="phone" verified={user.phone_verified} onVerified={onSaved} />}
        </div>
        {sensitiveChange && noPassword && (
          <div className="text-xs text-marigold">To change your email we'll send a code to your mobile number.</div>
        )}
        {sensitiveChange && !noPassword && (
          <div className="rounded-xl bg-marigold/5 border border-marigold/30 p-3">
            <Label className="text-marigold">Current password (required to change email/phone)</Label>
            <Input data-testid="profile-current-password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="h-11 mt-1 bg-white" />
          </div>
        )}
        <div className="pt-2">
          <Button onClick={submit} disabled={busy || !anyChange} data-testid="profile-save-btn" className="rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </div>
      </div>
      <PhoneOtpDialog open={!!otpFor} onOpenChange={(o) => !o && setOtpFor(null)}
        title={otpFor === "reauth" ? "Confirm it's you" : "Verify your mobile number"}
        lockedPhone={otpFor === "reauth" ? (user.phone || "").replace(/^\+91/, "") : undefined}
        onVerified={async (idToken) => {
          if (otpFor === "reauth") { setOtpFor(null); await save(idToken); return; }
          await api.post("/auth/phone/link", { id_token: idToken });
          toast.success("Mobile number verified");
          setOtpFor(null);
          await onSaved();
        }} />
    </Card>
  );
}

// G18: download your data, or delete the account.
/** Shops near one of my saved addresses may fill it in when they take my
 *  order by phone (they see the area until they save it; I'm told). */
function NearbyShopsCard() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const on = user?.nearby_shops_fill_address !== false;
  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch("/customer/privacy", { nearby_shops_fill_address: !on });
      await refresh();
      toast.success(on ? "Businesses will type your details themselves" : "Businesses you visit can fill in your details");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-6 rounded-2xl border-stone-200" data-testid="privacy-nearby-shops">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-heading font-semibold text-lg">Let shops and pet care businesses fill in my details</div>
          <p className="text-sm text-stone-600 mt-1">When you visit or phone a shop, groomer, vet or trainer, they type your mobile number. Pet care businesses then see your name and your pets' names, type, breed and size (never health or care notes). Shops see the area of a saved address that's in their delivery area, and nothing more until they save your delivery. We tell you each time.</p>
        </div>
        <button type="button" role="switch" aria-checked={on} disabled={busy} onClick={toggle} data-testid="privacy-nearby-toggle"
          className={`shrink-0 mt-1 h-7 w-12 rounded-full transition ${on ? "bg-teal" : "bg-stone-300"}`}>
          <span className={`block h-5 w-5 rounded-full bg-white shadow transition ${on ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
    </Card>
  );
}

/** Birthday wishes with an offer from shops and pet care businesses I've
 *  bought from or booked with. Off until I turn it on. */
function BirthdayOffersCard() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const on = user?.birthday_offers === true;
  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch("/customer/privacy", { birthday_offers: !on });
      await refresh();
      toast.success(on ? "No birthday offers" : "You'll get birthday offers for your pets");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-6 rounded-2xl border-stone-200" data-testid="privacy-birthday">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-heading font-semibold text-lg">Birthday offers for my pets</div>
          <p className="text-sm text-stone-600 mt-1">Shops and pet care businesses you've bought from or booked with can send a birthday wish with an offer when your pet's birthday is near (once a year each). Add your pets' birthdays under My pets.</p>
        </div>
        <button type="button" role="switch" aria-checked={on} disabled={busy} onClick={toggle} data-testid="privacy-birthday-toggle"
          className={`shrink-0 mt-1 h-7 w-12 rounded-full transition ${on ? "bg-teal" : "bg-stone-300"}`}>
          <span className={`block h-5 w-5 rounded-full bg-white shadow transition ${on ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
    </Card>
  );
}

function PrivacyCard() {
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const exportData = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/customer/account/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = `pawbazaar-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.post("/customer/account/delete", { password, confirm: confirmText });
      toast.success("Your account has been deleted");
      await logout();
      window.location.assign("/");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <NearbyShopsCard />
      <BirthdayOffersCard />
      <Card className="p-6 rounded-2xl border-stone-200">
        <div className="font-heading font-semibold text-lg">Download your data</div>
        <p className="text-sm text-stone-600 mt-1">Your profile, addresses, orders, subscriptions, reviews and help requests as a JSON file.</p>
        <Button onClick={exportData} disabled={busy} variant="outline" className="mt-3 rounded-full" data-testid="privacy-export">Download my data</Button>
      </Card>
      <Card className="p-6 rounded-2xl border-destructive/30">
        <div className="font-heading font-semibold text-lg text-destructive">Delete account</div>
        <p className="text-sm text-stone-600 mt-1">
          This removes your name, contact details, addresses and subscriptions and signs you out everywhere. Past orders are kept without your
          personal details because tax rules require us to. You can't delete the account while an order is on its way.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 mt-1" data-testid="privacy-delete-password" /></div>
          <div><Label>Type DELETE to confirm</Label><Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="h-11 mt-1" data-testid="privacy-delete-confirm" /></div>
        </div>
        <Button onClick={deleteAccount} disabled={busy || !password || confirmText !== "DELETE"} className="mt-3 rounded-full bg-destructive text-white hover:bg-destructive/90" data-testid="privacy-delete-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete my account"}
        </Button>
      </Card>
    </div>
  );
}

// G2: verify the email / mobile on file with a one-time code.
function VerifyField({ channel, verified, onVerified }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const label = channel === "email" ? "email" : "mobile number";

  if (verified) {
    return <div className="text-xs text-teal mt-1 flex items-center gap-1" data-testid={`profile-${channel}-verified`}><ShieldCheck className="h-3.5 w-3.5" /> Verified</div>;
  }
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify/send", { channel });
      setSent(true);
      if (data.sent === false) {
        toast.error(`We couldn't send the code to your ${label} right now. Please try again later.`);
      } else {
        toast.success(`We sent a code to your ${label}`);
      }
      if (data.debug_code) setCode(data.debug_code);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify/confirm", { channel, code });
      toast.success(`Your ${label} is verified`);
      setSent(false); setCode("");
      await onVerified();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid={`profile-${channel}-verify`}>
      <span className="text-xs text-stone-500">Not verified.</span>
      {sent ? (
        <>
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className="h-8 w-28 text-xs font-mono" data-testid={`profile-${channel}-code`} />
          <Button size="sm" variant="outline" disabled={busy || code.length !== 6} onClick={confirm} className="h-8 rounded-full text-xs">Confirm</Button>
          <button type="button" disabled={busy} onClick={send} className="text-xs text-stone-500 underline">Resend</button>
        </>
      ) : (
        <button type="button" disabled={busy} onClick={send} className="text-xs text-teal font-semibold hover:underline" data-testid={`profile-${channel}-send-code`}>Verify now</button>
      )}
    </div>
  );
}

// Accounts created with a mobile number + SMS code have no password yet.
function SetPasswordCard({ user, onSaved }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (next.length < 8) return toast.error("Use at least 8 characters");
    if (next !== confirm) return toast.error("The two passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/set-password", { new_password: next });
      toast.success("Password added — you can now also sign in with your email");
      setNext(""); setConfirm("");
      await onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-6 rounded-2xl border-stone-200 max-w-2xl" data-testid="set-password-card">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="h-5 w-5 text-teal" />
        <div className="font-heading text-xl font-semibold">Add a password</div>
      </div>
      <p className="text-sm text-stone-600 mb-4">You sign in with your mobile number and an SMS code. Add a password to also sign in with <b>{user.email}</b>.</p>
      <div className="space-y-4">
        <div><Label>New password</Label><Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className="h-11 mt-1" data-testid="sp-new" /></div>
        <div><Label>Repeat it</Label><Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 mt-1" data-testid="sp-confirm" /></div>
        <Button onClick={submit} disabled={busy || !next || !confirm} className="rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 font-semibold" data-testid="sp-save-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add password"}
        </Button>
      </div>
    </Card>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New password and confirmation do not match");
    setBusy(true);
    try {
      const { data: pwRes } = await api.post("/customer/change-password", { current_password: cur, new_password: next });
      // Other sessions are signed out; keep this one with the fresh token.
      if (pwRes?.token) setAccessToken(pwRes.token);
      toast.success("Password updated");
      setCur(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-2xl border-stone-200 max-w-2xl">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="h-5 w-5 text-teal" />
        <div className="font-heading text-xl font-semibold">Change password</div>
      </div>
      <div className="space-y-4">
        <div><Label>Current password</Label><Input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="h-11 mt-1" /></div>
        <div><Label>New password</Label><Input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="h-11 mt-1" /></div>
        <div><Label>Confirm new password</Label><Input data-testid="cp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11 mt-1" /></div>
        <div className="pt-2">
          <Button onClick={submit} disabled={busy || !cur || !next || !confirm} data-testid="cp-save-btn" className="rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function AddressesCard() {
  const [addresses, setAddresses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/customer/addresses"); setAddresses(data); }
    catch (e) { logger.warn("Failed to load addresses:", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.label}"?`)) return;
    try { await api.delete(`/customer/addresses/${a.id}`); toast.success("Address deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const setDefault = async (a) => {
    try { await api.post(`/customer/addresses/${a.id}/set-default`); toast.success("Default address updated"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <Card className="p-6 rounded-2xl border-stone-200">
      <div className="flex items-center justify-between mb-4">
        <div className="font-heading text-xl font-semibold">Saved addresses</div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-address-btn" onClick={() => setEditing(null)} className="rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
              <Plus className="h-4 w-4 mr-1" /> Add address
            </Button>
          </DialogTrigger>
          {dialogOpen && <AddressForm key={editing?.id || "new"} editing={editing} onDone={() => { setDialogOpen(false); setEditing(null); load(); }} testidPrefix="addr" />}
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-stone-500 py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
      ) : addresses.length === 0 ? (
        <div className="rounded-xl bg-stone-50 border border-dashed border-stone-300 p-8 text-center">
          <MapPin className="h-8 w-8 text-stone-400 mx-auto mb-2" />
          <p className="text-sm text-stone-600">No saved addresses yet. Add one to speed up checkout.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {addresses.map(a => (
            <div key={a.id} data-testid={`address-card-${a.id}`} className={`rounded-2xl border p-4 relative ${a.is_default ? "border-teal bg-teal/5" : "border-stone-200"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className={`border-0 rounded-full text-[10px] ${a.is_default ? "bg-teal text-white" : "bg-stone-200 text-stone-700"}`}>{a.label}</Badge>
                  {a.is_default && <Badge className="bg-teal/15 text-teal border-0 rounded-full text-[10px]"><Star className="h-2.5 w-2.5 mr-0.5" /> Default</Badge>}
                </div>
              </div>
              <div className="text-sm text-stone-700">{[a.flat_no, a.address].filter(Boolean).join(", ")}</div>
              {a.landmark && <div className="text-xs text-stone-500 mt-0.5">Near {a.landmark}</div>}
              <div className="text-xs text-stone-500 mt-1">{[a.area, a.city, a.state, a.pincode].filter(Boolean).join(" · ")}</div>
              {a.needs_location ? (
                <button type="button" onClick={() => { setEditing(a); setDialogOpen(true); }} data-testid={`address-confirm-pin-${a.id}`}
                  className="mt-2 rounded-full bg-marigold/15 text-marigold text-[10px] font-semibold px-2.5 py-1">
                  Confirm location on map
                </button>
              ) : a.serviceability_status === "active" ? (
                <Badge data-testid={`address-serviceability-${a.id}`} className="mt-2 bg-teal/15 text-teal border-0 rounded-full text-[10px]">Shops deliver here</Badge>
              ) : (
                <Badge data-testid={`address-serviceability-${a.id}`} className="mt-2 bg-terracotta/15 text-terracotta border-0 rounded-full text-[10px]">No shop delivers here yet</Badge>
              )}
              {a.maps_url && !a.needs_location && (
                <a href={a.maps_url} target="_blank" rel="noreferrer" className="ml-2 text-[10px] text-teal hover:underline">View on map</a>
              )}
              <div className="flex gap-1 mt-3">
                {!a.is_default && (
                  <Button size="sm" variant="ghost" onClick={() => setDefault(a)} data-testid={`address-set-default-${a.id}`} className="h-8 text-xs text-teal">
                    <Star className="h-3 w-3 mr-1" /> Set default
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setDialogOpen(true); }} data-testid={`address-edit-${a.id}`} className="h-8">
                  <Edit3 className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(a)} data-testid={`address-delete-${a.id}`} className="h-8 text-destructive">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

